import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Wallet, MessageCircle, Crown, Sparkles, ArrowLeft, Store as StoreIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchMyStore } from "@/lib/stores";
import { useAuth } from "@/hooks/use-auth";

import {
  useBalance,
  fetchMyTransactions,
  formatAzn,
  PROMO_DURATIONS,
  promoPrice,
  WHATSAPP_LINK,
} from "@/lib/balance";

export const Route = createFileRoute("/_authenticated/balans")({
  head: () => ({
    meta: [
      { title: "Balans — Albazar.az" },
      {
        name: "description",
        content:
          "Hesab balansınızı yoxlayın, WhatsApp vasitəsilə AZN yükləyin və elanlarınızı VIP və ya Premium edin.",
      },
      { property: "og:title", content: "Balans — Albazar.az" },
      {
        property: "og:description",
        content: "AZN balansı, ödəniş tarixçəsi və VIP/Premium paket qiymətləri.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BalancePage,
});

const KIND_LABEL: Record<string, string> = {
  topup: "Balans artımı",
  withdraw: "Balansdan çıxarış",
  promotion: "Elan reklamı",
  order: "Sifariş ödənişi",
  bonus: "Bonus",
};

function BalancePage() {
  const { user } = useAuth();
  const { data: balance, isLoading } = useBalance(user?.id);
  const { data: tx } = useQuery({
    queryKey: ["my-transactions", user?.id],
    enabled: Boolean(user?.id),
    queryFn: () => fetchMyTransactions(user!.id),
  });
  const { data: store } = useQuery({
    queryKey: ["my-store", user?.id],
    enabled: Boolean(user?.id),
    queryFn: () => fetchMyStore(user!.id),
  });

  const totals = (tx ?? []).reduce(
    (acc, t) => {
      const amount = Number(t.amount) || 0;
      if (t.kind === "promotion") acc.promotion += Math.abs(amount);
      else if (t.kind === "topup") acc.topup += amount;
      else if (amount > 0) acc.order += amount;
      return acc;
    },
    { topup: 0, order: 0, promotion: 0 },
  );


  return (
    <div className="container-x max-w-3xl py-6">
      <Link to="/profil" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
        <ArrowLeft className="size-4" /> Kabinetə qayıt
      </Link>

      <h1 className="mt-4 text-2xl font-extrabold tracking-tight">Balans</h1>

      <div className="mt-4 rounded-3xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Wallet className="size-6" />
          </span>
          <div>
            <p className="text-sm text-muted-foreground">Cari balansınız</p>
            <p className="text-3xl font-black leading-tight">
              {isLoading ? "…" : formatAzn(balance ?? 0)}
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Balans artırmaq üçün WhatsApp-dan bizə yazın. Ödənişdən sonra admin məbləği
          hesabınıza əlavə edəcək və balansı VIP/Premium elanlar üçün istifadə edə biləcəksiniz.
        </p>

        <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="mt-4 block">
          <Button className="h-12 w-full rounded-2xl bg-success text-success-foreground text-base font-bold hover:bg-success/90">
            <MessageCircle className="size-5" /> WhatsApp-a yaz (AZN al)
          </Button>
        </a>
      </div>

      {store && (
        <section className="mt-6 rounded-3xl border border-info/40 bg-card p-6">
          <h2 className="flex items-center gap-2 text-lg font-extrabold">
            <StoreIcon className="size-5 text-info" /> Mağaza hesabatı
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            «{store.name}» mağazası üzrə daxilolmalar və xərclər.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-border p-4">
              <p className="text-xs text-muted-foreground">Admin tərəfindən yüklənən</p>
              <p className="mt-1 text-xl font-black text-success">{formatAzn(totals.topup)}</p>
            </div>
            <div className="rounded-2xl border border-border p-4">
              <p className="text-xs text-muted-foreground">Sifarişlər üzrə daxilolma</p>
              <p className="mt-1 text-xl font-black text-success">{formatAzn(totals.order)}</p>
            </div>
            <div className="rounded-2xl border border-border p-4">
              <p className="text-xs text-muted-foreground">Elan təqdimatı xərci</p>
              <p className="mt-1 text-xl font-black text-destructive">{formatAzn(totals.promotion)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Sifariş və satış pulları admin tərəfindən yoxlanıldıqdan sonra balansınıza əlavə edilir.
            Ödəniş üçün WhatsApp-a yazın.
          </p>
        </section>
      )}


      <section className="mt-6 rounded-3xl border border-border bg-card p-6">
        <h2 className="text-lg font-extrabold">Paket qiymətləri</h2>

        <p className="mt-4 flex items-center gap-2 text-sm font-bold">
          <Crown className="size-4 text-[hsl(42_92%_52%)]" /> VIP paket
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          {PROMO_DURATIONS.map((d) => (
            <div key={d.days} className="rounded-2xl border border-border p-4 text-center">
              <p className="text-sm text-muted-foreground">{d.label}</p>
              <p className="mt-1 text-2xl font-black">{promoPrice("vip", d.days)} AZN</p>
            </div>
          ))}
        </div>

        <p className="mt-5 flex items-center gap-2 text-sm font-bold">
          <Sparkles className="size-4 text-info" /> Premium paket
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          {PROMO_DURATIONS.map((d) => (
            <div key={d.days} className="rounded-2xl border border-info/40 p-4 text-center">
              <p className="text-sm text-muted-foreground">{d.label}</p>
              <p className="mt-1 text-2xl font-black">{promoPrice("premium", d.days)} AZN</p>
            </div>
          ))}
        </div>

        <p className="mt-3 text-sm text-muted-foreground">
          Paketlər balansdan ödənilir. Balansı WhatsApp vasitəsilə artıra bilərsiniz.
        </p>
      </section>

      <section className="mt-6 rounded-3xl border border-border bg-card p-6">
        <h2 className="text-lg font-extrabold">Əməliyyat tarixçəsi</h2>
        {!tx?.length ? (
          <p className="mt-3 text-sm text-muted-foreground">Hələ əməliyyat yoxdur.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border">
            {tx.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">
                    {KIND_LABEL[t.kind] ?? t.kind}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {new Date(t.created_at).toLocaleString("az-AZ")}
                    {t.note ? ` • ${t.note}` : ""}
                  </p>
                </div>
                <span
                  className={
                    Number(t.amount) >= 0
                      ? "shrink-0 font-bold text-success"
                      : "shrink-0 font-bold text-destructive"
                  }
                >
                  {Number(t.amount) >= 0 ? "+" : ""}
                  {formatAzn(Number(t.amount))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}